"use client";

import { useEffect, useState, useRef } from "react";
import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { titleToSlug } from "@/lib/utils";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { UserURL } from "@/functions";
import { cn } from "@/lib/utils";
import { IBlog, IUserData } from "@/types";
import { IconChevronRight } from "@tabler/icons-react";
import UserImage from "../profile/heading/sections/UserImage";

interface BlogBreadcrumbProps {
    user: IUserData;
    blog?: IBlog;
    category: any;
    headerElementId?: string;
    post?: boolean;
    children?: React.ReactNode;
    v2Theme?: IUserData["chosen_theme"]["blog"];
}

export const BlogBreadcrumb: React.FC<BlogBreadcrumbProps> = ({
    user,
    blog,
    category,
    post = false,
    headerElementId = "blog-header",
    children,
    v2Theme,
}) => {
    const [atTop, setAtTop] = useState(true);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [categoriesWithPosts, setCategoriesWithPosts] = useState<any[]>([]);

    // Hover states for different elements
    const [isHomeHovered, setIsHomeHovered] = useState(false);
    const [isBlogsHovered, setIsBlogsHovered] = useState(false);
    const [isCategoryHovered, setIsCategoryHovered] = useState(false);

    useEffect(() => {
        // Find the parent container with overflow-auto
        const findScrollContainer = () => {
            // Start with the document element
            let element = document.querySelector(".noscroll");

            if (element) {
                containerRef.current = element as HTMLDivElement;
                return;
            }

            // Fallback to document if no specific container found
            containerRef.current =
                document.documentElement as unknown as HTMLDivElement;
        };

        findScrollContainer();

        // Set up Intersection Observer for the header element
        const headerElement = document.getElementById(headerElementId);

        if (headerElement) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    // When the header is not intersecting (not visible), set atTop to false
                    const isVisible = entries[0]?.isIntersecting;
                    setAtTop(isVisible);
                },
                {
                    threshold: 0,
                    rootMargin: "-1px 0px 0px 0px", // Trigger as soon as the element starts to leave
                }
            );

            observerRef.current.observe(headerElement);
        } else {
            // Fallback to scroll position if header element not found
            const handleScroll = () => {
                if (containerRef.current) {
                    const scrollTop = containerRef.current.scrollTop;
                    setAtTop(scrollTop <= 100);
                }
            };

            // Set initial state
            handleScroll();

            // Add event listener to the container
            if (containerRef.current) {
                containerRef.current.addEventListener("scroll", handleScroll);
            }

            // Clean up
            return () => {
                if (containerRef.current) {
                    containerRef.current.removeEventListener(
                        "scroll",
                        handleScroll
                    );
                }
            };
        }

        // Clean up observer
        return () => {
            if (observerRef.current && headerElement) {
                observerRef.current.unobserve(headerElement);
                observerRef.current.disconnect();
            }
        };
    }, [headerElementId]);

    // style
    let font_style: any = {};
    if (v2Theme) {
        font_style = {
            fontFamily: v2Theme.design.font.heading,
        };
    } else {
        if (!user.theme.font || user.theme.font.font.length == 0) {
            font_style = {};
        } else {
            font_style = {
                fontFamily: user.theme.font?.font,
            };
        }
    }

    // Add text color from the user's theme
    if (user.theme.text_col_heading) {
        font_style.color = v2Theme
            ? v2Theme.colors.heading_text
            : user.theme.text_col_heading;
    }

    // Hover styles for links
    const homeLinkStyle = {
        ...font_style,
        transform: isHomeHovered ? "translateY(-2px)" : "translateY(0)",
        textDecoration: isHomeHovered ? "underline" : "none",
        transition: "all 0.2s ease",
    };

    const blogsLinkStyle = {
        ...font_style,
        transform: isBlogsHovered ? "translateY(-2px)" : "translateY(0)",
        textDecoration: isBlogsHovered ? "underline" : "none",
        transition: "all 0.2s ease",
    };

    const categoryBadgeStyle = {
        ...font_style,
        background: v2Theme
            ? v2Theme.colors.primary
            : user.theme.accent_col || user.theme.button,
        color: v2Theme
            ? v2Theme.colors.primary_text
            : user.theme.text_col_heading,
        transform: isCategoryHovered ? "scale(1.05)" : "scale(1)",
        boxShadow: isCategoryHovered ? "0 4px 8px rgba(0,0,0,0.1)" : "none",
        transition: "all 0.2s ease",
    };

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
    let backgroundValue;

    switch (true) {
        // Case 1: Not at the top - use theme button
        case !atTop:
            backgroundValue = v2Theme
                ? v2Theme.colors.primary
                : user.theme.accent_col;
            break;

        // Case 2: On a post page with empty blog theme background - use theme button
        case !atTop && post && blog?.theme.background === "":
            backgroundValue = v2Theme
                ? v2Theme.colors.primary
                : user.theme.button;
            break;
        case atTop && post && blog?.theme.background !== "":
            backgroundValue = v2Theme
                ? v2Theme.colors.primary
                : blog?.theme.background;
            break;

        // Default case: use transparent
        default:
            backgroundValue = "transparent";
            break;
    }

    return (
        <motion.div
            className={cn(
                `noscroll sticky top-0 z-40 flex items-center gap-2 transition-all duration-150`,
                atTop ? "" : "!bg-opacity-50 shadow-md",
                "rounded-xl",
                "!rounded-t-none"
            )}
            style={{
                background: backgroundValue,
            }}
            animate={{
                padding: atTop ? "0.5rem" : "0.5rem",
            }}
            transition={{ duration: 0.3 }}
        >
            <Breadcrumb>
                <BreadcrumbList className="mr-auto w-full justify-start">
                    <BreadcrumbItem
                        className={cn(
                            atTop && "!hidden !w-0 !max-w-0 !opacity-0"
                        )}
                    >
                        <BreadcrumbLink
                            style={homeLinkStyle}
                            className={cn(
                                " flex items-center gap-1 truncate text-xl font-semibold",
                                atTop && "!hidden !w-0 !max-w-0 !opacity-0"
                            )}
                            href={"https://" + UserURL(user)}
                            onMouseEnter={() => setIsHomeHovered(true)}
                            onMouseLeave={() => setIsHomeHovered(false)}
                        >
                            <UserImage
                                className={cn(
                                    "transition-all duration-300",
                                    atTop ? "!hidden" : "h-10 w-10",
                                    atTop && post
                                        ? "h-10 w-10 opacity-100"
                                        : "h-10 w-10 opacity-100",
                                    isHomeHovered && "rotate-6"
                                )}
                                user={user}
                            />

                            {user.displayname}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator
                        className={cn(
                            post && atTop && "!text-foreground/0",
                            atTop && "!hidden !w-0 !max-w-0 !opacity-0"
                        )}
                    >
                        <IconChevronRight
                            size={20}
                            style={font_style}
                            className={cn(
                                post &&
                                    atTop &&
                                    "pointer-events-none !text-foreground/0"
                            )}
                        />
                    </BreadcrumbSeparator>
                    <BreadcrumbItem
                        className={cn(
                            atTop && "!hidden !w-0 !max-w-0 !opacity-0"
                        )}
                    >
                        <BreadcrumbLink
                            style={blogsLinkStyle}
                            className={cn(
                                "text-xl font-semibold transition-all duration-300",

                                post &&
                                    atTop &&
                                    "pointer-events-none !text-foreground/0"
                            )}
                            href={"https://" + UserURL(user) + "/blog"}
                        >
                            Blog
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator
                        className={cn(
                            post && "hidden",
                            post &&
                                atTop &&
                                "pointer-events-none !text-foreground/0",
                            atTop && "!hidden !w-0 !max-w-0 !opacity-0"
                        )}
                    >
                        <IconChevronRight
                            size={20}
                            style={font_style}
                            className={cn(
                                post && "hidden",
                                post &&
                                    atTop &&
                                    "pointer-events-none !text-foreground/0"
                            )}
                        />
                    </BreadcrumbSeparator>
                    <BreadcrumbItem
                        className={cn(
                            post && "hidden",
                            post &&
                                atTop &&
                                "pointer-events-none !text-foreground/0"
                        )}
                    >
                        <DropdownMenu
                            open={dropdownOpen}
                            onOpenChange={toggleDropdown}
                        >
                            <DropdownMenuTrigger
                                className={cn(
                                    "flex items-center gap-1 data-[state=open]:rotate-90",
                                    post &&
                                        atTop &&
                                        "pointer-events-none !bg-foreground/0 !text-foreground/0"
                                )}
                                asChild
                                onMouseEnter={() => setIsCategoryHovered(true)}
                                onMouseLeave={() => setIsCategoryHovered(false)}
                            >
                                <Badge
                                    className={`mx-auto w-fit cursor-pointer rounded-xl text-center text-xl transition-all duration-200`}
                                    style={categoryBadgeStyle}
                                >
                                    <span>
                                        {category?.name || post
                                            ? category.name
                                            : "All Posts"}
                                    </span>
                                    <ChevronRight
                                        className={cn(
                                            "transition-transform duration-200",
                                            isCategoryHovered && "rotate-90",
                                            dropdownOpen && "rotate-90"
                                        )}
                                    />
                                </Badge>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="start"
                                className="text-lg font-semibold"
                                style={{
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : user.theme.button,
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : user.theme.text_col_button,
                                }}
                            >
                                {blog?.categories.map((cat: any) => (
                                    <DropdownMenuItem key={cat.id} asChild>
                                        <a
                                            href={`https://${UserURL(user)}/blog/${titleToSlug(cat.name.toLowerCase())}`}
                                            className="transition-colors hover:cursor-pointer hover:bg-card/40 hover:underline focus:bg-card/40"
                                            style={{
                                                color: v2Theme
                                                    ? v2Theme.colors
                                                          .primary_text
                                                    : user.theme
                                                          .text_col_button,
                                            }}
                                        >
                                            {cat.name}
                                        </a>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            {children}
        </motion.div>
    );
};
